import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { useToggle } from '@ucheba/utils/hooks/useToggle'
import { IFacet } from '@ucheba/utils/types/search'
import { useRouter } from '@ucheba/utils/hooks/useRouter'
import isEqual from '@ucheba/utils/helpers/core/isEqual'
import {
  IFormikContext,
  IUseFacetCore,
  IUseFacetFormCore,
  IUseFacetItemWrapperCore,
} from './types'
import { updateSticky } from '../Sticky'

export const useFacetCore: IUseFacetCore = (props) => {
  const { controlName, isRadio, isForcedArray, onSubmit, initialValues } = props

  /** Подготавливает выбранные пункты */
  const initialValue = useMemo(() => {
    if (initialValues) {
      return {
        [controlName]: initialValues,
      }
    }

    return {
      [controlName]: isRadio ? '' : [],
    }
  }, [controlName, isRadio, initialValues])

  /* При сабмите подготавливаем значения формы и вызываем колбэк сабмита */
  const onSubmitForm = useCallback(
    (values) => {
      const submitValue = { ...values }

      if (isForcedArray) {
        submitValue[controlName] = submitValue[controlName]
          ? [submitValue[controlName]]
          : []
      }

      onSubmit(submitValue[controlName])
    },
    [controlName, isForcedArray, onSubmit]
  )

  return {
    initialValue,
    onSubmitForm,
  }
}

export const useFacetFormCore: IUseFacetFormCore = (props) => {
  const {
    items,
    maxItems,
    scrollWrapperRef,
    controlName,
    isSelect = false,
    isSorted = true,
  } = props
  const [maxItemsVisible, setMaxItemsVisible] = useState(maxItems || items.length)
  const [isExpanded, setIsExpanded] = useToggle(isSelect)
  const [scrollWrapperMaxHeight, setScrollWrapperMaxHeight] = useState('100%')
  const { query } = useRouter()
  const [sortedItems, setSortedItems] = useState<IFacet[]>(items || [])
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { values: formValues }: IFormikContext = useFormikContext()
  const [values, setValues] = useState(formValues)

  // useMemo(() => {
  //   if (!isEqual(values, formValues)) {
  //     setValues(values)
  //   }
  // }, [values])

  useMemo(() => {
    if (!isEqual(values, formValues)) {
      setValues(formValues)
    }
  }, [formValues])

  /* При обновлении квери параметров сбрасываем состояние отсортированных фасетов,
   * чтобы новые фасеты заново отсортировались */

  useEffect(() => {
    if (!isExpanded) {
      setSortedItems([])
    }
  }, [query, items, values])

  /* Сортируем относительно выбранных, но только при первой инициализации */
  useEffect(() => {
    /* Сортируем только если еще не сортировали и если блок фасетов закрыт */
    if (sortedItems.length || isExpanded) return

    let checked = query[controlName] || values[controlName]
    const hasChecked = Array.isArray(checked) ? checked.length : checked

    if (hasChecked) {
      // TODO: нужно сделать логику, когда есть вложенность
      const sorted = items.reduce((acc, item) => {
        const itemValue = String(item.value)

        if (Array.isArray(checked)) {
          checked = checked.map((el) => String(el))
        }

        if (checked?.length && checked.includes(itemValue)) {
          acc.unshift(item)
        } else {
          acc.push(item)
        }

        return acc
      }, [] as IFacet[])

      setSortedItems(sorted)
    } else {
      setSortedItems(items)
    }
  }, [items, isExpanded, controlName, sortedItems.length, query, values])

  /* Сортируем по алфавиту при раскрытии */
  useEffect(() => {
    if (isExpanded && isSorted) {
      setSortedItems(
        items.slice().sort((item1, item2) => {
          if (item1.name < item2.name) return -1
          if (item1.name > item2.name) return 1
          return 0
        })
      )
    }
  }, [isExpanded, items, isSorted])

  /* Возвращает видимы массив элементов */
  const visibleItems = useMemo(() => {
    return maxItems && !isExpanded ? sortedItems.slice(0, maxItemsVisible) : sortedItems
  }, [isExpanded, maxItems, maxItemsVisible, sortedItems])

  /* Обновляем Стики при изменении количества айтемов */
  useEffect(() => {
    updateSticky()
  }, [sortedItems])

  /* Установить высоту контейнера фасета */
  const setScrollWrapperHeight = useCallback(() => {
    const maxHeight = Array.from(scrollWrapperRef.current?.children || []).reduce(
      (sum, element) => {
        const style = getComputedStyle(element)
        const marginTop = parseInt(style.marginTop, 10)
        const marginBottom = parseInt(style.marginBottom, 10)

        // eslint-disable-next-line no-param-reassign
        sum += element.clientHeight + marginTop + marginBottom

        return sum
      },
      0
    )

    setScrollWrapperMaxHeight(`${maxHeight}px`)
  }, [scrollWrapperRef])

  /* Если не раскрыт фасет, то устанавливаем высоту в 100% */
  useEffect(() => {
    if (!isExpanded) {
      setScrollWrapperMaxHeight('100%')
    }
  }, [isExpanded])

  /* Метод раскрытия ограниченного списка фасетов */
  const onExpand = useCallback(() => {
    setScrollWrapperHeight()
    setMaxItemsVisible(items.length)
    setIsExpanded(true)
    updateSticky()
  }, [items.length, setIsExpanded, setScrollWrapperHeight])

  return {
    visibleItems,
    onExpand,
    isExpanded,
    scrollWrapperMaxHeight,
  }
}

export const useFacetItemWrapperCore: IUseFacetItemWrapperCore = (props) => {
  const { controlName, isRadio, isForcedArray } = props

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { values, setValues }: IFormikContext = useFormikContext()

  useEffect(() => {
    if (isForcedArray && Array.isArray(values[controlName])) {
      setValues({
        [controlName]: values[controlName]?.[0] || '',
      })
    }
  }, [controlName, isForcedArray, setValues, values])

  /* Формируем текущее значение фасета */
  const value = useMemo(
    () => values[controlName] || (isRadio ? '' : []),
    [controlName, isRadio, values]
  )

  /* Определяем, нужно ли сделать фасет раскрытым, если у него два уровня */
  const getOpenStatus = useCallback(
    (childrenItems) =>
      childrenItems.some((item) => {
        return isRadio ? value === String(item.value) : value.includes(String(item.value))
      }),
    [isRadio, value]
  )

  /* Обновляем позицию стики, когда открывается фасет */
  const onDropdownOpen = useCallback(() => setTimeout(updateSticky, 100), [])

  return {
    onDropdownOpen,
    getOpenStatus,
  }
}
